/**
 * Qué portada sirve este hostname.
 *
 * ## Por qué esto existe
 *
 * `inversionistas.ilumetria.com` tiene que abrir el área y no la portada de
 * marketing. Pero **el bundle es el mismo y la distribución de CloudFront es la
 * misma** para todos los nombres que cuelgan de `ilumetria.com`: hoy responden
 * `www.`, `cuponera.` y cualquier nombre inventado, porque el alias de la
 * distribución de producción es el comodín `*.ilumetria.com`. Comprobado:
 * `zzz-no-existe-9x.ilumetria.com` contesta 200 con TLS válido.
 *
 * ## Por qué lista blanca y nunca «lo que no sea www»
 *
 * Con la regla negativa, **el día que alguien apunte cualquier nombre nuevo a
 * esa distribución, ese nombre aterriza en el área de inversionistas**. Y como
 * el comodín ya resuelve, «apuntar un nombre nuevo» no requiere tocar nada:
 * basta con teclearlo. La lista blanca invierte eso — lo que no está escrito
 * aquí ve el sitio público, que es lo seguro.
 *
 * ## Lo que esta función NO hace
 *
 * No cierra el área. Las rutas `/inversionistas/*` responden igual en
 * `www.ilumetria.com`, y eso es deliberado: un enlace profundo guardado tiene
 * que seguir funcionando. **La puerta cambia la portada, no protege el
 * contenido** — el bundle es el mismo y cerrarlo por hostname sería una falsa
 * protección. Quien protege es el guardia de sesión, y el contenido de verdad
 * lo cierra el servidor.
 */

/**
 * Los dos nombres del área. Exactos, nunca sufijos.
 *
 * `inversionistas-dev` lleva guión y no punto porque el comodín de Route 53
 * **cubre un solo nivel**: `inversionistas.dev.ilumetria.com` resuelve pero
 * falla la verificación TLS. Comprobado el 2026-09-20.
 */
const HOSTS_DEL_AREA = [
  'inversionistas.ilumetria.com',
  'inversionistas-dev.ilumetria.com',
] as const;

/**
 * Normaliza lo que trae `window.location.hostname` antes de comparar.
 *
 * Los tres recortes son casos reales, no defensa por si acaso:
 *
 * - **minúsculas**: un `Host` puede llegar en mayúsculas y el DNS no distingue;
 * - **el punto final**: `inversionistas.ilumetria.com.` es un FQDN válido y una
 *   cadena distinta;
 * - **el puerto**: en local el nombre llega como `…:5173`.
 */
function normalizar(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/\.$/, '').split(':')[0];
}

/**
 * Comparación **exacta**, nunca `endsWith` ni `startsWith`.
 *
 * `inversionistas.ilumetria.com.atacante.test` termina distinto pero empieza
 * igual, y `evil-inversionistas.ilumetria.com` al revés. Con igualdad exacta no
 * hay familia de nombres que se cuele.
 */
export function esPortadaDeInversionistas(hostname: string): boolean {
  const limpio = normalizar(hostname);
  return (HOSTS_DEL_AREA as readonly string[]).includes(limpio);
}

/**
 * El hostname donde el área **de verdad funciona** en producción.
 *
 * No es cosmética: la API de `capital` declara sus orígenes CORS uno a uno y
 * en prod admite **sólo** `https://inversionistas.ilumetria.com`. Desde
 * `www.ilumetria.com` la pantalla se pinta igual —el bundle y la distribución
 * de CloudFront son los mismos— y el navegador bloquea la llamada al entrar.
 *
 * Lo que se ve entonces es «No se pudo contactar el servidor. Revisa tu
 * conexión», que manda a mirar el wifi por un problema de configuración.
 */
export const HOST_DEL_AREA_EN_PROD = 'inversionistas.ilumetria.com';

/**
 * Los hostnames donde el área funciona además de los del área.
 *
 * `localhost` está porque los orígenes CORS de **dev** lo incluyen a propósito
 * (`http://localhost:5173` y `:4173`), para poder recorrer el área entera
 * contra la API de dev sin desplegar el front.
 */
const LOCALES = ['localhost', '127.0.0.1'];

/** Si el área puede hablar con su API desde este hostname. */
export function elAreaFuncionaAqui(hostname: string): boolean {
  const limpio = normalizar(hostname);
  return esPortadaDeInversionistas(limpio) || LOCALES.includes(limpio);
}

/**
 * A dónde mandar a quien llegó al área por un hostname que no la sirve.
 *
 * Devuelve `null` cuando **no hay a dónde mandarlo**, y ese caso existe:
 * `dev.ilumetria.com` tendría que ir a `inversionistas-dev.ilumetria.com`, que
 * **no existe todavía** —el comodín de Route 53 cubre un solo nivel y ese
 * nombre pide tocar el certificado de dev—. Mandar a alguien a un host que no
 * resuelve es peor que dejarlo donde está: se cambia un error explicable por
 * uno que parece que el sitio entero se cayó.
 *
 * Todo lo demás bajo `ilumetria.com` apunta a la distribución de producción
 * —`www.`, `cuponera.` y cualquier nombre inventado, por el comodín— así que
 * su área es la de producción.
 */
export function aDondeVaElArea(hostname: string): string | null {
  const limpio = normalizar(hostname);
  if (elAreaFuncionaAqui(limpio)) return null;
  if (limpio === 'dev.ilumetria.com') return null;
  if (!limpio.endsWith('ilumetria.com')) return null;
  return HOST_DEL_AREA_EN_PROD;
}

/* ------------------------------------------------------------------------- *
 * La plataforma MICE (Platanometría), que NO se sirve desde este bundle
 * ------------------------------------------------------------------------- */

/**
 * Los dos nombres de la plataforma MICE, que es otro producto y otro origen.
 *
 * `plataforma-dev` lleva guión por el mismo motivo que `inversionistas-dev`: el
 * comodín de Route 53 **cubre un solo nivel**, así que
 * `plataforma.dev.ilumetria.com` resuelve y falla la verificación TLS.
 *
 * > **El de dev no está decidido todavía** —`documentacion/productos/mice/
 * > NOTA-HOSTNAMES-PARA-EL-ALTA.md` §2 lo deja abierto y lo llama «la salida
 * > análoga»—. Se escribe aquí con la forma con guión, que es la única que el
 * > comodín cubre, y se corrige el día del alta si `mice` elige otra.
 */
const HOST_DE_LA_PLATAFORMA_EN_PROD = 'plataforma.ilumetria.com';
const HOST_DE_LA_PLATAFORMA_EN_DEV = 'plataforma-dev.ilumetria.com';

/**
 * La ruta de la pantalla de acceso **dentro de la plataforma**.
 *
 * No tiene nada que ver con la `/login` de `routes.tsx`, que es una de las doce
 * rutas retiradas con el backend el 2026-09-18 y redirige al home a propósito.
 * Ésta cuelga de otro origen, la sirve `mice-web` y la escribe otro equipo.
 */
export const RUTA_LOGIN_DE_LA_PLATAFORMA = '/login';

/**
 * Los hostnames de **este** sitio que son de desarrollo.
 *
 * Lista blanca y comparación exacta, igual que `HOSTS_DEL_AREA` y por la misma
 * razón: con el comodín `*.ilumetria.com` cualquier nombre inventado ya
 * resuelve, así que una regla negativa —«lo que no sea www es dev»— mandaría a
 * dev a todo lo que nadie configuró.
 *
 * `localhost` entra porque quien corre `npm run dev` está trabajando contra
 * dev, no contra la plataforma que ven los clientes.
 */
const HOSTS_DE_DEV = [
  'dev.ilumetria.com',
  'inversionistas-dev.ilumetria.com',
  'localhost',
  '127.0.0.1',
];

/**
 * A dónde manda el botón de la plataforma desde este hostname.
 *
 * **Devuelve siempre un hostname, nunca `null`, y ahí se separa de
 * `aDondeVaElArea`.** El área vive en este mismo bundle, así que desde el
 * hostname donde funciona no hay a dónde ir: `null` significa «quédate, usa un
 * `<Link>`». La plataforma es otro producto en otro origen —`mice-web`—, y
 * desde aquí nunca se está dentro: el enlace es absoluto siempre.
 *
 * Lo que sí se conserva del área es el motivo de fondo: **el destino se deriva,
 * no se teclea**. Un `https://plataforma.ilumetria.com/login` escrito dentro de
 * un componente manda a producción desde `dev.ilumetria.com` y nadie lo nota,
 * porque la página abre igual.
 */
export function aDondeVaLaPlataforma(hostname: string): string {
  const limpio = normalizar(hostname);
  if (HOSTS_DE_DEV.includes(limpio)) return HOST_DE_LA_PLATAFORMA_EN_DEV;
  return HOST_DE_LA_PLATAFORMA_EN_PROD;
}

/** La URL completa del acceso a la plataforma desde este hostname. */
export function enlaceALaPlataforma(hostname: string): string {
  return `https://${aDondeVaLaPlataforma(hostname)}${RUTA_LOGIN_DE_LA_PLATAFORMA}`;
}
